import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/api";

/**
 * Tavolata by IntegraNube
 * src/Pages/Cocina.js
 *
 * KDS conectado al backend Restaurant real.
 *
 * Endpoints:
 *   GET   /restaurante/cocina/comandas?estado=ACTIVAS
 *   PATCH /restaurante/cocina/comandas/:id/recibir
 *   PATCH /restaurante/cocina/items/:id/preparar
 *   PATCH /restaurante/cocina/items/:id/listo
 *
 * Estados esperados:
 *   Comanda: PENDIENTE -> RECIBIDA -> EN_PREPARACION -> LISTA
 *   Item:    ENVIADO/PENDIENTE -> RECIBIDO -> EN_PREPARACION -> LISTO
 *
 * La autenticación JWT/tenantid la agrega src/api/api.js.
 */

const REFRESH_MS = 5000;
const WARN_MINUTES = 10;
const DANGER_MINUTES = 20;

function first(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function errorMessage(error, fallback = "No fue posible completar la operación.") {
  return (
    error?.friendlyMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizeState(value) {
  const state = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  if (!state) return "PENDIENTE";
  if (["PREPARANDO", "PREPARACION", "EN_PREPARACIÓN"].includes(state)) {
    return "EN_PREPARACION";
  }
  if (["LISTA", "READY"].includes(state)) return "LISTO";
  if (["RECIBIDA"].includes(state)) return "RECIBIDA";
  return state;
}

function extractArray(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const sources = [
    payload,
    payload?.data,
    payload?.result,
    payload?.cocina,
    payload?.data?.cocina,
  ];

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const key of keys) {
      if (Array.isArray(source[key])) return source[key];
    }
  }

  return [];
}

function normalizeModifiers(raw) {
  const mods = extractArray(raw, [
    "modificadores",
    "modifiers",
    "opciones",
    "item_modificadores",
  ]);

  return mods
    .filter((mod) => mod?.visible_cocina !== false)
    .map((mod, index) => ({
      id: String(first(mod?.id, mod?.opcion_id, index)),
      grupo: String(first(mod?.grupo_nombre, mod?.grupo, "") || ""),
      nombre: String(
        first(
          mod?.opcion_nombre,
          mod?.nombre,
          mod?.opcion?.nombre,
          `Opción ${index + 1}`
        )
      ),
      cantidad: Math.max(1, num(first(mod?.cantidad, 1), 1)),
    }));
}

function normalizeItem(raw, index = 0) {
  return {
    id: String(
      first(
        raw?.id,
        raw?.comanda_item_id,
        raw?.pedido_item_id,
        raw?.item_id,
        `item-${index}`
      )
    ),
    actionId: String(
      first(
        raw?.id,
        raw?.comanda_item_id,
        raw?.pedido_item_id,
        raw?.item_id,
        `item-${index}`
      )
    ),
    pedidoItemId: first(raw?.pedido_item_id, raw?.pedidoItemId, raw?.id),
    productoId: first(raw?.producto_id, raw?.productoId),
    nombre: String(
      first(
        raw?.nombre_snapshot,
        raw?.producto_nombre,
        raw?.nombre,
        raw?.producto?.nombre,
        "Producto"
      )
    ),
    cantidad: Math.max(0, num(first(raw?.cantidad, 1), 1)),
    asiento: first(raw?.asiento, raw?.seat),
    tiempoServicio: Math.max(1, num(first(raw?.tiempo_servicio, 1), 1)),
    observacion: String(first(raw?.observacion, raw?.nota, "") || ""),
    estado: normalizeState(first(raw?.estado, raw?.item_estado, "PENDIENTE")),
    modificadores: normalizeModifiers(raw),
    raw,
  };
}

function normalizeComanda(raw, index = 0) {
  const rawItems = extractArray(raw, [
    "items",
    "comanda_items",
    "detalle",
    "lineas",
    "productos",
  ]);

  return {
    id: String(first(raw?.id, raw?.comanda_id, `comanda-${index}`)),
    pedidoId: String(first(raw?.pedido_id, raw?.pedidoId, "") || ""),
    pedidoNumero: String(
      first(raw?.pedido_numero, raw?.numero_pedido, raw?.pedido?.numero, "") || ""
    ),
    mesaId: String(first(raw?.mesa_id, raw?.mesa?.id, "") || ""),
    mesaCodigo: String(
      first(raw?.mesa_codigo, raw?.mesa?.codigo, "") || ""
    ),
    mesaNombre: String(
      first(
        raw?.mesa_nombre,
        raw?.mesa?.nombre,
        raw?.tipo === "MESA" ? "Mesa" : raw?.tipo,
        "Pedido"
      )
    ),
    zonaNombre: String(
      first(raw?.zona_nombre, raw?.zona?.nombre, "") || ""
    ),
    estacionId: String(
      first(raw?.estacion_id, raw?.estacion?.id, "") || ""
    ),
    estacionCodigo: String(
      first(raw?.estacion_codigo, raw?.estacion?.codigo, "") || ""
    ),
    estacionNombre: String(
      first(
        raw?.estacion_nombre,
        raw?.estacion?.nombre,
        raw?.estacion_codigo,
        "Cocina"
      )
    ),
    meseroNombre: String(
      first(
        raw?.mesero_nombre,
        raw?.usuario_nombre,
        raw?.mesero?.nombre,
        ""
      ) || ""
    ),
    estado: normalizeState(first(raw?.estado, "PENDIENTE")),
    enviadaAt: first(
      raw?.enviada_at,
      raw?.enviado_at,
      raw?.created_at,
      raw?.fecha_creacion
    ),
    recibidaAt: first(raw?.recibida_at, raw?.received_at),
    preparacionAt: first(raw?.preparacion_at, raw?.preparando_at),
    listaAt: first(raw?.lista_at, raw?.listo_at),
    items: rawItems.map(normalizeItem),
    raw,
  };
}

function extractComandas(payload) {
  return extractArray(payload, ["comandas", "rows", "items"]).map(
    normalizeComanda
  );
}

function minutesSince(value, now = Date.now()) {
  if (!value) return 0;
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return 0;
  return Math.max(0, Math.floor((now - time) / 60000));
}

function urgency(minutes) {
  if (minutes >= DANGER_MINUTES) return "danger";
  if (minutes >= WARN_MINUTES) return "warning";
  return "normal";
}

function itemIsPending(item) {
  return ["PENDIENTE", "ENVIADO", "RECIBIDO", "RECIBIDA"].includes(item.estado);
}

function itemIsPreparing(item) {
  return item.estado === "EN_PREPARACION";
}

function itemIsReady(item) {
  return ["LISTO", "LISTA"].includes(item.estado);
}

function itemIsCancelled(item) {
  return ["CANCELADO", "CANCELADA"].includes(item.estado);
}

function criticalObservation(text) {
  const value = String(text || "").toUpperCase();
  return (
    value.includes("ALERG") ||
    value.includes("CONDICIÓN") ||
    value.includes("CONDICION") ||
    value.includes("⚠")
  );
}

function formatTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleTimeString("es-CL", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function buildKitchenIndicator(comandas, now = Date.now()) {
  const activas = comandas.filter(
    (comanda) =>
      !["LISTA", "LISTO", "CERRADA", "CANCELADA", "CANCELADO"].includes(
        comanda.estado
      )
  );

  const atrasadas = activas.filter(
    (comanda) => minutesSince(comanda.enviadaAt, now) >= DANGER_MINUTES
  );

  const pendientes = activas.filter(
    (comanda) => comanda.estado === "PENDIENTE"
  );

  const preparando = activas.filter(
    (comanda) =>
      comanda.estado === "EN_PREPARACION" ||
      comanda.items.some(itemIsPreparing)
  );

  let label = "Sin comandas";
  let tone = "neutral";

  if (atrasadas.length) {
    label = `${atrasadas.length} atrasada${atrasadas.length === 1 ? "" : "s"}`;
    tone = "danger";
  } else if (pendientes.length) {
    label = `${pendientes.length} nueva${pendientes.length === 1 ? "" : "s"}`;
    tone = "warning";
  } else if (preparando.length) {
    label = `${preparando.length} preparando`;
    tone = "warning";
  }

  return {
    count: activas.length,
    label,
    tone,
    showZero: false,
  };
}

export default function Cocina() {
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  const knownIdsRef = useRef(new Set());

  const [comandas, setComandas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  const [estacionActiva, setEstacionActiva] = useState("TODAS");
  const [estadoFiltro, setEstadoFiltro] = useState("TODAS");
  const [actionIds, setActionIds] = useState(() => new Set());
  const [notice, setNotice] = useState(null);
  const [newAlert, setNewAlert] = useState(0);

  const showNotice = useCallback((type, message, duration = 3500) => {
    setNotice({ type, message });
    window.clearTimeout(showNotice._timer);
    showNotice._timer = window.setTimeout(() => {
      if (mountedRef.current) setNotice(null);
    }, duration);
  }, []);

  const publishIndicator = useCallback((rows) => {
    const cocina = buildKitchenIndicator(rows);

    window.dispatchEvent(
      new CustomEvent("tavolata:indicators", {
        detail: { cocina },
      })
    );
  }, []);

  const loadComandas = useCallback(
    async ({ silent = false, skipNewAlert = false } = {}) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);

      try {
        const response = await api.get("/restaurante/cocina/comandas", {
          params: { estado: "ACTIVAS" },
        });

        const rows = extractComandas(response.data);

        if (!mountedRef.current) return;

        if (initializedRef.current && !skipNewAlert) {
          const currentIds = new Set(rows.map((comanda) => comanda.id));
          const nuevos = rows.filter(
            (comanda) =>
              !knownIdsRef.current.has(comanda.id) &&
              comanda.estado === "PENDIENTE"
          );

          if (nuevos.length) {
            setNewAlert(nuevos.length);
            showNotice(
              "warning",
              `${nuevos.length} nueva${nuevos.length === 1 ? "" : "s"} comanda${nuevos.length === 1 ? "" : "s"} recibida${nuevos.length === 1 ? "" : "s"}.`,
              5000
            );

            if (typeof navigator.vibrate === "function") {
              navigator.vibrate([120, 80, 120]);
            }
          }

          knownIdsRef.current = currentIds;
        } else {
          knownIdsRef.current = new Set(rows.map((comanda) => comanda.id));
          initializedRef.current = true;
        }

        setComandas(rows);
        setOnline(true);
        setLastUpdate(new Date());
        publishIndicator(rows);
      } catch (error) {
        if (!navigator.onLine) setOnline(false);

        if (!silent) {
          showNotice(
            "error",
            errorMessage(
              error,
              "No fue posible obtener las comandas activas de Cocina."
            )
          );
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [publishIndicator, showNotice]
  );

  useEffect(() => {
    mountedRef.current = true;
    loadComandas();

    return () => {
      mountedRef.current = false;
      window.clearTimeout(showNotice._timer);
    };
  }, [loadComandas, showNotice]);

  useEffect(() => {
    const refresh = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        loadComandas({ silent: true });
      }
    }, REFRESH_MS);

    return () => window.clearInterval(refresh);
  }, [loadComandas]);

  useEffect(() => {
    const clock = window.setInterval(() => {
      setNowTick(Date.now());
    }, 30000);

    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      loadComandas({ silent: true });
    };
    const onOffline = () => setOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [loadComandas]);

  const estaciones = useMemo(() => {
    const map = new Map();

    comandas.forEach((comanda) => {
      const id = comanda.estacionId || comanda.estacionNombre;
      if (!id) return;
      if (!map.has(id)) {
        map.set(id, {
          id,
          nombre: comanda.estacionNombre || "Cocina",
        });
      }
    });

    return Array.from(map.values()).sort((a, b) =>
      a.nombre.localeCompare(b.nombre, "es")
    );
  }, [comandas]);

  useEffect(() => {
    if (
      estacionActiva !== "TODAS" &&
      !estaciones.some((estacion) => estacion.id === estacionActiva)
    ) {
      setEstacionActiva("TODAS");
    }
  }, [estaciones, estacionActiva]);

  const metrics = useMemo(() => {
    const items = comandas.flatMap((comanda) => comanda.items);

    return {
      comandas: comandas.filter(
        (comanda) =>
          !["LISTA", "LISTO", "CERRADA", "CANCELADA"].includes(comanda.estado)
      ).length,
      nuevas: comandas.filter((comanda) => comanda.estado === "PENDIENTE").length,
      preparando: items.filter(itemIsPreparing).length,
      listos: items.filter(itemIsReady).length,
      atrasadas: comandas.filter(
        (comanda) =>
          !["LISTA", "LISTO", "CERRADA", "CANCELADA"].includes(comanda.estado) &&
          minutesSince(comanda.enviadaAt, nowTick) >= DANGER_MINUTES
      ).length,
    };
  }, [comandas, nowTick]);

  const comandasVisibles = useMemo(() => {
    let rows = comandas;

    if (estacionActiva !== "TODAS") {
      rows = rows.filter(
        (comanda) =>
          String(comanda.estacionId || comanda.estacionNombre) ===
          String(estacionActiva)
      );
    }

    if (estadoFiltro === "NUEVAS") {
      rows = rows.filter((comanda) => comanda.estado === "PENDIENTE");
    } else if (estadoFiltro === "PREPARANDO") {
      rows = rows.filter(
        (comanda) =>
          comanda.estado === "EN_PREPARACION" ||
          comanda.items.some(itemIsPreparing)
      );
    } else if (estadoFiltro === "LISTOS") {
      rows = rows.filter((comanda) => comanda.items.some(itemIsReady));
    } else if (estadoFiltro === "ATRASADAS") {
      rows = rows.filter(
        (comanda) =>
          minutesSince(comanda.enviadaAt, nowTick) >= DANGER_MINUTES
      );
    }

    return [...rows].sort((a, b) => {
      const aMinutes = minutesSince(a.enviadaAt, nowTick);
      const bMinutes = minutesSince(b.enviadaAt, nowTick);

      const aPending = a.estado === "PENDIENTE" ? 0 : 1;
      const bPending = b.estado === "PENDIENTE" ? 0 : 1;

      if (aPending !== bPending) return aPending - bPending;
      return bMinutes - aMinutes;
    });
  }, [comandas, estacionActiva, estadoFiltro, nowTick]);

  const setAction = useCallback((id, active) => {
    setActionIds((current) => {
      const next = new Set(current);
      if (active) next.add(String(id));
      else next.delete(String(id));
      return next;
    });
  }, []);

  const receiveComanda = useCallback(
    async (comanda, { silentSuccess = false } = {}) => {
      const id = comanda?.id;
      if (!id) return false;

      setAction(`comanda:${id}`, true);

      try {
        await api.patch(`/restaurante/cocina/comandas/${id}/recibir`);
        if (!silentSuccess) showNotice("success", "Comanda recibida.");
        return true;
      } catch (error) {
        showNotice(
          "error",
          errorMessage(error, "No fue posible recibir la comanda.")
        );
        return false;
      } finally {
        setAction(`comanda:${id}`, false);
      }
    },
    [setAction, showNotice]
  );

  const prepareItem = useCallback(
    async (comanda, item) => {
      if (!item?.actionId) return;

      setAction(`item:${item.actionId}`, true);

      try {
        if (comanda?.estado === "PENDIENTE") {
          const received = await receiveComanda(comanda, {
            silentSuccess: true,
          });
          if (!received) return;
        }

        await api.patch(
          `/restaurante/cocina/items/${item.actionId}/preparar`
        );

        showNotice("success", `${item.nombre}: en preparación.`);
        await loadComandas({ silent: true, skipNewAlert: true });
      } catch (error) {
        showNotice(
          "error",
          errorMessage(error, "No fue posible iniciar la preparación.")
        );
      } finally {
        setAction(`item:${item.actionId}`, false);
      }
    },
    [loadComandas, receiveComanda, setAction, showNotice]
  );

  const markReady = useCallback(
    async (item) => {
      if (!item?.actionId) return;

      setAction(`item:${item.actionId}`, true);

      try {
        await api.patch(`/restaurante/cocina/items/${item.actionId}/listo`);
        showNotice("success", `${item.nombre}: listo para servir.`);
        await loadComandas({ silent: true, skipNewAlert: true });

        window.dispatchEvent(
          new CustomEvent("tavolata:notifications-refresh")
        );
      } catch (error) {
        showNotice(
          "error",
          errorMessage(error, "No fue posible marcar el producto como listo.")
        );
      } finally {
        setAction(`item:${item.actionId}`, false);
      }
    },
    [loadComandas, setAction, showNotice]
  );

  return (
    <div className="kds-shell">
      <style>{`
        * { box-sizing: border-box; }

        :root {
          --kds-bg: #f4f5f3;
          --kds-paper: #ffffff;
          --kds-ink: #17211c;
          --kds-muted: #69746d;
          --kds-line: #e2e7e3;
          --kds-brand: #e68d2f;
          --kds-brand-dark: #b96c1d;
          --kds-green: #158354;
          --kds-green-soft: #e6f7ee;
          --kds-amber: #ad6d0d;
          --kds-amber-soft: #fff3d7;
          --kds-red: #c7393f;
          --kds-red-soft: #ffebec;
          --kds-blue: #2e70ba;
          --kds-blue-soft: #edf5ff;
          --kds-shadow: 0 10px 30px rgba(31, 42, 35, .075);
        }

        .kds-shell {
          min-height: 100vh;
          min-height: 100dvh;
          background: var(--kds-bg);
          color: var(--kds-ink);
          font-family:
            Inter, ui-sans-serif, system-ui, -apple-system,
            BlinkMacSystemFont, "Segoe UI", sans-serif;
        }

        .kds-page {
          width: min(1760px, 100%);
          margin: 0 auto;
          padding: 16px;
          padding-bottom: calc(22px + env(safe-area-inset-bottom));
        }

        button { font: inherit; }

        .kds-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 12px;
        }

        .kds-title h1 {
          margin: 0;
          font-size: clamp(24px, 2.7vw, 34px);
          letter-spacing: -.04em;
          line-height: 1;
        }

        .kds-title p {
          margin: 6px 0 0;
          color: var(--kds-muted);
          font-size: 13px;
        }

        .kds-head-actions {
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .kds-online {
          min-height: 42px;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 0 12px;
          border: 1px solid var(--kds-line);
          border-radius: 999px;
          background: #fff;
          color: var(--kds-muted);
          font-size: 12px;
          font-weight: 850;
        }

        .kds-online-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: ${online ? "var(--kds-green)" : "var(--kds-red)"};
        }

        .kds-refresh {
          min-height: 42px;
          border: 1px solid var(--kds-line);
          border-radius: 11px;
          padding: 0 13px;
          background: #fff;
          color: #48544d;
          font-weight: 850;
          cursor: pointer;
        }

        .kds-metrics {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 9px;
          margin-bottom: 12px;
        }

        .kds-metric {
          min-width: 0;
          padding: 11px 13px;
          border: 1px solid var(--kds-line);
          border-radius: 14px;
          background: #fff;
          box-shadow: 0 4px 14px rgba(31,42,35,.035);
        }

        .kds-metric small {
          display: block;
          color: var(--kds-muted);
          font-size: 11px;
          font-weight: 800;
        }

        .kds-metric strong {
          display: block;
          margin-top: 2px;
          font-size: 23px;
          line-height: 1;
        }

        .kds-metric.alert {
          border-color: #edb9bc;
          background: #fff8f8;
        }

        .kds-metric.alert strong {
          color: var(--kds-red);
        }

        .kds-toolbar {
          overflow: hidden;
          margin-bottom: 12px;
          border: 1px solid var(--kds-line);
          border-radius: 15px;
          background: #fff;
          box-shadow: var(--kds-shadow);
        }

        .kds-toolbar-row {
          display: flex;
          align-items: center;
          gap: 8px;
          overflow-x: auto;
          padding: 9px 11px;
          scrollbar-width: thin;
        }

        .kds-toolbar-row + .kds-toolbar-row {
          border-top: 1px solid var(--kds-line);
        }

        .kds-toolbar-label {
          flex: 0 0 auto;
          min-width: 62px;
          color: var(--kds-muted);
          font-size: 11px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: .04em;
        }

        .kds-filter {
          min-height: 40px;
          flex: 0 0 auto;
          padding: 0 13px;
          border: 1px solid #dae0dc;
          border-radius: 10px;
          background: #fff;
          color: #4d5952;
          font-weight: 850;
          cursor: pointer;
        }

        .kds-filter.active {
          border-color: #dfad75;
          background: #fff3e7;
          color: var(--kds-brand-dark);
        }

        .kds-new-alert {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 12px;
          padding: 11px 14px;
          border: 1px solid #edc980;
          border-radius: 13px;
          background: #fff6dc;
          color: #805210;
          font-weight: 850;
        }

        .kds-new-alert button {
          border: 0;
          background: transparent;
          color: inherit;
          font-weight: 900;
          cursor: pointer;
        }

        .kds-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
          gap: 12px;
          align-items: start;
        }

        .kds-ticket {
          overflow: hidden;
          border: 1px solid var(--kds-line);
          border-radius: 16px;
          background: #fff;
          box-shadow: var(--kds-shadow);
        }

        .kds-ticket.warning {
          border-color: #eccb86;
        }

        .kds-ticket.danger {
          border-color: #e59b9f;
          box-shadow: 0 0 0 2px rgba(199,57,63,.055), var(--kds-shadow);
        }

        .kds-ticket-head {
          padding: 13px 14px 11px;
          border-bottom: 1px solid var(--kds-line);
        }

        .kds-ticket-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }

        .kds-place {
          min-width: 0;
        }

        .kds-place strong {
          display: block;
          font-size: 18px;
          line-height: 1.15;
        }

        .kds-place small {
          display: block;
          margin-top: 4px;
          color: var(--kds-muted);
          font-size: 11px;
        }

        .kds-time {
          min-height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
          padding: 0 10px;
          border-radius: 999px;
          background: #eef2ef;
          color: #526058;
          font-size: 13px;
          font-weight: 950;
        }

        .kds-time.warning {
          background: var(--kds-amber-soft);
          color: var(--kds-amber);
        }

        .kds-time.danger {
          background: var(--kds-red-soft);
          color: var(--kds-red);
        }

        .kds-ticket-meta {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          margin-top: 9px;
        }

        .kds-chip {
          min-height: 27px;
          display: inline-flex;
          align-items: center;
          padding: 0 8px;
          border-radius: 999px;
          background: #f1f4f2;
          color: #566159;
          font-size: 10px;
          font-weight: 850;
        }

        .kds-chip.station {
          background: var(--kds-blue-soft);
          color: var(--kds-blue);
        }

        .kds-receive {
          width: 100%;
          min-height: 44px;
          margin-top: 10px;
          border: 0;
          border-radius: 11px;
          background: var(--kds-brand);
          color: #fff;
          font-weight: 950;
          cursor: pointer;
        }

        .kds-receive:disabled {
          opacity: .55;
          cursor: not-allowed;
        }

        .kds-items {
          padding: 5px 13px 10px;
        }

        .kds-item {
          padding: 12px 1px;
          border-bottom: 1px solid #edf0ee;
        }

        .kds-item:last-child {
          border-bottom: 0;
        }

        .kds-item.cancelled {
          opacity: .48;
        }

        .kds-item-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }

        .kds-item-name {
          min-width: 0;
          font-size: 15px;
          line-height: 1.25;
          font-weight: 950;
        }

        .kds-item-qty {
          color: var(--kds-brand-dark);
          font-size: 17px;
        }

        .kds-item-state {
          flex: 0 0 auto;
          min-height: 26px;
          display: inline-flex;
          align-items: center;
          padding: 0 8px;
          border-radius: 999px;
          background: #f0f3f1;
          color: #59645d;
          font-size: 9px;
          font-weight: 900;
        }

        .kds-item-state.preparing {
          background: var(--kds-amber-soft);
          color: var(--kds-amber);
        }

        .kds-item-state.ready {
          background: var(--kds-green-soft);
          color: var(--kds-green);
        }

        .kds-modifiers {
          margin-top: 8px;
          display: grid;
          gap: 5px;
        }

        .kds-modifier {
          padding: 7px 9px;
          border-radius: 9px;
          background: #f7f8f7;
          color: #465149;
          font-size: 12px;
          font-weight: 750;
        }

        .kds-modifier strong {
          color: var(--kds-ink);
        }

        .kds-observation {
          margin-top: 8px;
          padding: 9px 10px;
          border-left: 4px solid var(--kds-amber);
          border-radius: 8px;
          background: #fff8e8;
          color: #654410;
          font-size: 12px;
          font-weight: 850;
          line-height: 1.35;
        }

        .kds-observation.critical {
          border-left-color: var(--kds-red);
          background: #fff0f1;
          color: #8b272c;
        }

        .kds-item-actions {
          display: flex;
          gap: 8px;
          margin-top: 10px;
        }

        .kds-action {
          min-height: 42px;
          flex: 1;
          border: 1px solid #dae0dc;
          border-radius: 10px;
          background: #fff;
          color: #465249;
          font-weight: 900;
          cursor: pointer;
        }

        .kds-action.prepare {
          border-color: #e6c17a;
          background: #fff8e9;
          color: #945e0d;
        }

        .kds-action.ready {
          border-color: #9fd4b9;
          background: #eaf9f0;
          color: #116b40;
        }

        .kds-action:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .kds-ticket-foot {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          padding: 9px 14px;
          border-top: 1px solid var(--kds-line);
          background: #fafbfa;
          color: var(--kds-muted);
          font-size: 10px;
          font-weight: 800;
        }

        .kds-empty,
        .kds-loading {
          min-height: 340px;
          display: grid;
          place-items: center;
          padding: 30px;
          text-align: center;
          color: var(--kds-muted);
        }

        .kds-empty strong {
          display: block;
          color: #445049;
          font-size: 18px;
          margin-bottom: 5px;
        }

        .kds-notice {
          position: fixed;
          z-index: 1500;
          right: 18px;
          bottom: calc(18px + env(safe-area-inset-bottom));
          max-width: min(430px, calc(100vw - 36px));
          padding: 12px 15px;
          border-radius: 12px;
          color: #fff;
          font-weight: 850;
          box-shadow: 0 16px 44px rgba(20,30,24,.22);
        }

        .kds-notice.success { background: var(--kds-green); }
        .kds-notice.warning { background: #a7670b; }
        .kds-notice.error { background: var(--kds-red); }

        @media (max-width: 900px) {
          .kds-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .kds-metrics {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }

          .kds-metric:nth-child(4),
          .kds-metric:nth-child(5) {
            display: none;
          }
        }

        @media (max-width: 650px) {
          .kds-page {
            padding: 10px;
          }

          .kds-head {
            align-items: center;
          }

          .kds-online {
            display: none;
          }

          .kds-grid {
            grid-template-columns: 1fr;
          }

          .kds-toolbar-label {
            min-width: 52px;
          }

          .kds-ticket {
            border-radius: 14px;
          }
        }

        @media (max-width: 430px) {
          .kds-metrics {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 6px;
          }

          .kds-metric {
            padding: 9px;
          }

          .kds-metric strong {
            font-size: 20px;
          }

          .kds-head-actions {
            gap: 5px;
          }

          .kds-refresh {
            padding: 0 10px;
          }
        }
      `}</style>

      <main className="kds-page">
        <header className="kds-head">
          <div className="kds-title">
            <h1>Cocina</h1>
            <p>
              Comandas activas en tiempo real
              {lastUpdate
                ? ` · actualizado ${lastUpdate.toLocaleTimeString("es-CL", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : ""}
            </p>
          </div>

          <div className="kds-head-actions">
            <div className="kds-online">
              <span className="kds-online-dot" />
              {online ? "Online" : "Sin conexión"}
            </div>

            <button
              type="button"
              className="kds-refresh"
              onClick={() => loadComandas({ silent: true })}
              disabled={refreshing}
            >
              {refreshing ? "Actualizando…" : "Actualizar"}
            </button>
          </div>
        </header>

        <section className="kds-metrics">
          <div className="kds-metric">
            <small>Comandas activas</small>
            <strong>{metrics.comandas}</strong>
          </div>
          <div className="kds-metric">
            <small>Nuevas</small>
            <strong>{metrics.nuevas}</strong>
          </div>
          <div className="kds-metric">
            <small>Preparando</small>
            <strong>{metrics.preparando}</strong>
          </div>
          <div className="kds-metric">
            <small>Ítems listos</small>
            <strong>{metrics.listos}</strong>
          </div>
          <div className={`kds-metric ${metrics.atrasadas ? "alert" : ""}`}>
            <small>Atrasadas +{DANGER_MINUTES} min</small>
            <strong>{metrics.atrasadas}</strong>
          </div>
        </section>

        <section className="kds-toolbar">
          <div className="kds-toolbar-row">
            <span className="kds-toolbar-label">Estación</span>

            <button
              type="button"
              className={`kds-filter ${estacionActiva === "TODAS" ? "active" : ""}`}
              onClick={() => setEstacionActiva("TODAS")}
            >
              Todas
            </button>

            {estaciones.map((estacion) => (
              <button
                type="button"
                key={estacion.id}
                className={`kds-filter ${estacionActiva === estacion.id ? "active" : ""}`}
                onClick={() => setEstacionActiva(estacion.id)}
              >
                {estacion.nombre}
              </button>
            ))}
          </div>

          <div className="kds-toolbar-row">
            <span className="kds-toolbar-label">Vista</span>

            {[
              ["TODAS", "Todas"],
              ["NUEVAS", `Nuevas${metrics.nuevas ? ` · ${metrics.nuevas}` : ""}`],
              ["PREPARANDO", "Preparando"],
              ["LISTOS", "Listos"],
              ["ATRASADAS", `Atrasadas${metrics.atrasadas ? ` · ${metrics.atrasadas}` : ""}`],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={`kds-filter ${estadoFiltro === value ? "active" : ""}`}
                onClick={() => setEstadoFiltro(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {newAlert > 0 && (
          <div className="kds-new-alert">
            <span>
              Nueva actividad: {newAlert} comanda{newAlert === 1 ? "" : "s"} esperando recepción.
            </span>
            <button type="button" onClick={() => setNewAlert(0)}>
              Ocultar
            </button>
          </div>
        )}

        {loading ? (
          <div className="kds-loading">Cargando comandas activas…</div>
        ) : comandasVisibles.length ? (
          <section className="kds-grid">
            {comandasVisibles.map((comanda) => {
              const elapsed = minutesSince(comanda.enviadaAt, nowTick);
              const level = urgency(elapsed);
              const receiveBusy = actionIds.has(`comanda:${comanda.id}`);

              const validItems = comanda.items.filter(
                (item) => !itemIsCancelled(item)
              );
              const readyCount = validItems.filter(itemIsReady).length;

              return (
                <article
                  className={`kds-ticket ${level}`}
                  key={comanda.id}
                >
                  <div className="kds-ticket-head">
                    <div className="kds-ticket-top">
                      <div className="kds-place">
                        <strong>
                          {comanda.mesaNombre}
                          {comanda.mesaCodigo &&
                          !comanda.mesaNombre.includes(comanda.mesaCodigo)
                            ? ` · ${comanda.mesaCodigo}`
                            : ""}
                        </strong>
                        <small>
                          {comanda.pedidoNumero
                            ? `Pedido #${comanda.pedidoNumero}`
                            : `Pedido ${comanda.pedidoId || "Restaurant"}`}
                          {comanda.zonaNombre ? ` · ${comanda.zonaNombre}` : ""}
                        </small>
                      </div>

                      <span className={`kds-time ${level}`}>
                        {elapsed} min
                      </span>
                    </div>

                    <div className="kds-ticket-meta">
                      <span className="kds-chip station">
                        {comanda.estacionNombre}
                      </span>
                      <span className="kds-chip">
                        {comanda.estado.replaceAll("_", " ")}
                      </span>
                      <span className="kds-chip">
                        {readyCount}/{validItems.length} listos
                      </span>
                    </div>

                    {comanda.estado === "PENDIENTE" && (
                      <button
                        type="button"
                        className="kds-receive"
                        onClick={async () => {
                          const ok = await receiveComanda(comanda);
                          if (ok) {
                            await loadComandas({
                              silent: true,
                              skipNewAlert: true,
                            });
                          }
                        }}
                        disabled={receiveBusy}
                      >
                        {receiveBusy ? "Recibiendo…" : "Recibir comanda"}
                      </button>
                    )}
                  </div>

                  <div className="kds-items">
                    {comanda.items.length ? (
                      comanda.items.map((item) => {
                        const busy = actionIds.has(`item:${item.actionId}`);
                        const preparing = itemIsPreparing(item);
                        const ready = itemIsReady(item);
                        const cancelled = itemIsCancelled(item);

                        return (
                          <div
                            className={`kds-item ${cancelled ? "cancelled" : ""}`}
                            key={item.id}
                          >
                            <div className="kds-item-top">
                              <div className="kds-item-name">
                                <span className="kds-item-qty">
                                  {item.cantidad}×
                                </span>{" "}
                                {item.nombre}
                                {item.asiento ? ` · P${item.asiento}` : ""}
                              </div>

                              <span
                                className={[
                                  "kds-item-state",
                                  preparing ? "preparing" : "",
                                  ready ? "ready" : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              >
                                {item.estado.replaceAll("_", " ")}
                              </span>
                            </div>

                            {item.modificadores.length > 0 && (
                              <div className="kds-modifiers">
                                {item.modificadores.map((mod) => (
                                  <div
                                    className="kds-modifier"
                                    key={mod.id}
                                  >
                                    {mod.grupo ? (
                                      <strong>{mod.grupo}: </strong>
                                    ) : null}
                                    {mod.cantidad > 1
                                      ? `${mod.cantidad}× `
                                      : ""}
                                    {mod.nombre}
                                  </div>
                                ))}
                              </div>
                            )}

                            {item.observacion && (
                              <div
                                className={[
                                  "kds-observation",
                                  criticalObservation(item.observacion)
                                    ? "critical"
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join(" ")}
                              >
                                {criticalObservation(item.observacion)
                                  ? "⚠ "
                                  : ""}
                                {item.observacion}
                              </div>
                            )}

                            {!cancelled && !ready && (
                              <div className="kds-item-actions">
                                {itemIsPending(item) && (
                                  <button
                                    type="button"
                                    className="kds-action prepare"
                                    onClick={() =>
                                      prepareItem(comanda, item)
                                    }
                                    disabled={busy}
                                  >
                                    {busy
                                      ? "Procesando…"
                                      : "Comenzar preparación"}
                                  </button>
                                )}

                                {preparing && (
                                  <button
                                    type="button"
                                    className="kds-action ready"
                                    onClick={() => markReady(item)}
                                    disabled={busy}
                                  >
                                    {busy
                                      ? "Procesando…"
                                      : "✓ Marcar listo"}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="kds-empty" style={{ minHeight: 120 }}>
                        La comanda no contiene ítems.
                      </div>
                    )}
                  </div>

                  <footer className="kds-ticket-foot">
                    <span>
                      Enviada {formatTime(comanda.enviadaAt) || "—"}
                    </span>
                    <span>
                      {comanda.meseroNombre
                        ? `Mesero: ${comanda.meseroNombre}`
                        : `Comanda ${comanda.id.slice(0, 8)}`}
                    </span>
                  </footer>
                </article>
              );
            })}
          </section>
        ) : (
          <div className="kds-empty">
            <div>
              <strong>Sin comandas para esta vista</strong>
              <span>
                Cocina se actualizará automáticamente cuando Mesero envíe nuevos productos.
              </span>
            </div>
          </div>
        )}
      </main>

      {notice && (
        <div className={`kds-notice ${notice.type}`}>
          {notice.message}
        </div>
      )}
    </div>
  );
}
